import {Link, withPrefix} from 'gatsby';
import {useStaticQuery, graphql} from 'gatsby';

import Layout from '../components/layout';

import {GlobalHead} from '../utils/head-utils';
import {Accordion} from '@trussworks/react-uswds';
import CaseListGrid from '../components/Tables/case-list-grid';
import {getRegionName} from '../utils/abbreviation-utils';
import {region_arr} from '../utils/text-utils';
import DataCurrencyFootnoteExcavation from '../components/data-currency-footnote-excavation';
import RawDataDownloadLink from '../components/raw-data-download-link';

import customMatcherFilter from '../utils/custom-filters/custom-matcher-filter';
import customDropdownFloatingFilter from '../utils/custom-filters/custom-dropdown-floating-filter';
import {renderLinkedCellData, renderLinkedCellDataNewWindow} from '../utils/render-link-utils';
import customTextFilter from '../utils/custom-filters/custom-text-filter';
import textFloatingFilter from '../utils/custom-filters/custom-text-floating-filter';
import {GatsbyImage, getImage} from 'gatsby-plugin-image';

const GetEnforcementPageQueryData = () => {
  const data = useStaticQuery(graphql`
    query ExcavationAndOldActionsQuery {
      postgres {
        excavation_cases: scCasesList(filter: {typeOfCase: {includes: "Excavation"}}, orderBy: OPENED_DT_DESC) {
          cpfNum
          operatorName
        }
      }
      site {
        siteMetadata {
          actions_2001_and_older {
            id
            region
            documentType
            caseNumber
            documentUrl
            operator
          }
        }
      }
      # filter out svgs because gatsby-plugin-sharp cannot process them
      allFile(filter: {extension: {nin: "svg"}}) {
        nodes {
          name
          childImageSharp {
            gatsbyImageData
          }
        }
      }
    }
  `);
  return data;
};

const linkList = () => {
  const cards = [
    {
      header: 'Enforcement Activity',
      card_img_alt: 'pipeline landscape',
      card_key: 'enforcement-activity',
      links: [{linkText: 'Cases Initiated and Closed by Year', linkUrl: '/summaries'}],
    },
    {
      header: 'Enforcement Actions',
      card_img_alt: 'person inspecting pipelines',
      card_key: 'enforcement-actions',
      links: [
        {linkText: 'Cases Initiated by Case Type', linkUrl: '/actions/cases-initiated'},
        {linkText: 'Orders Issued by Order Type', linkUrl: '/actions/orders'},
        {linkText: 'Cases Closed by Case Type', linkUrl: '/actions/cases-closed'},
      ],
    },
    {
      header: 'Enforcement Case Status by Year',
      card_img_alt: 'pipeline landscape',
      card_key: 'enforcement-case-status',
      links: [
        {linkText: 'Overall Case Status', linkUrl: '/case-status/ALL'},
        {linkText: 'Corrective Action Orders', linkUrl: '/case-status/CAO'},
        {linkText: 'Notices of Proposed Safety Order', linkUrl: '/case-status/PSO'},
        {linkText: 'Notices of Probable Violation', linkUrl: '/case-status/NOPV'},
        {linkText: 'Notices of Amendment', linkUrl: '/case-status/NOA'},
      ],
    },
    {
      header: 'Civil Penalty Cases',
      card_img_alt: 'pipeline warning plaque',
      card_key: 'civil-penalty-cases',
      links: [
        {linkText: 'Civil Penalty Cases Initiated', linkUrl: '/civil-penalties/cases-initiated'},
        {linkText: 'Civil Penalty Cases Resolved', linkUrl: '/civil-penalties/cases-resolved'},
        {linkText: 'Civil Penalties Resolved', linkUrl: '/civil-penalties/penalties-resolved'},
        {linkText: 'Civil Penalty Cases Resolved by DOJ', linkUrl: '/civil-penalties/doj-cases'},
      ],
    },
    {
      header: 'Cases Initiated',
      card_img_alt: 'person welding',
      card_key: 'cases-initiated',
      links: [
        {linkText: 'Listing of Cases Initiated', linkUrl: '/cases'},
        {linkText: 'Corrective Action Orders', linkUrl: '/cases/CAO'},
        {linkText: 'Notices of Proposed Safety Order', linkUrl: '/cases/PSO'},
        {linkText: 'Notices of Probable Violation', linkUrl: '/cases/NOPV'},
        {linkText: 'Warning Letters', linkUrl: '/cases/WL'},
        {linkText: 'Notices of Amendment', linkUrl: '/cases/NOA'},
      ],
    },
    {
      header: 'Lookup',
      card_img_alt: 'person typing on a laptop',
      card_key: 'lookup',
      links: [
        {linkText: 'Enforcement by Operator', linkUrl: '/operators'},
        {linkText: 'Cited Regulations', linkUrl: '/cited-regulations'},
        {linkText: '30 Day Incident Reports', linkUrl: '/incident-report'},
        {linkText: 'Enforcement Case Document Search', linkUrl: '/search'},
      ],
    },
  ];

  const image_names = GetEnforcementPageQueryData();

  return (
    <div className="margin-top-3" id="nav-cards">
      <ul className="usa-card-group">
        {cards.map((card) => {
          const image = getImage(image_names.allFile.nodes.find((node) => node.name == card.card_key));
          return (
            <li key={card.card_key} className="usa-card tablet:grid-col-6 desktop:grid-col-4">
              <div className="usa-card__container">
                <div className="usa-card__header">
                  <h2 className="usa-card__heading">{card.header}</h2>
                </div>
                <div className="usa-card__media height-card overflow-hidden">
                  <div className="usa-card__img height-full width-full">
                    <GatsbyImage image={image} alt={card.card_img_alt} className="height-full width-full" />
                  </div>
                </div>
                <ul className="usa-card__body">
                  {card.links.map((link) => (
                    <li key={link.linkUrl} className="usa-list usa-list--unstyled">
                      <Link to={link.linkUrl}>{link.linkText}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const rawDataAccordion = {
  title: <div style={{display: 'flex', justifyContent: 'space-between'}}>Enforcement Raw Data</div>,
  content: (
    <>
      <h4>How to Access Files</h4>

      <p>To load the data file into MS Excel:</p>
      <ol>
        <li>
          Download &ldquo;Enforcement Cases Initiated from 2002&rdquo; under the &ldquo;Enforcement Cases Initiated from
          2002&rdquo;.
        </li>
        <li>Open Excel and click &ldquo;Open&rdquo;. This will start the &ldquo;Open&rdquo; dialog box.</li>
        <li>Change file type to &ldquo;All Files&rdquo;.</li>
        <li>Select the data TXT file and click &ldquo;Open&rdquo;.</li>
        <li>Excel will start the Text Import Wizard and display the first dialog.</li>
        <li>
          Under &ldquo;Choose the file type that best describes your data:&rdquo;, select &ldquo;Delimited&rdquo;, click
          next.
        </li>
        <li>In the second dialog, select &ldquo;Tab&rdquo; under &ldquo;Delimiters&rdquo;.</li>
        <li>Uncheck &ldquo;Treat consecutive delimiters as one&rdquo;, click next.</li>
        <li>In the third dialog set &ldquo;Column data format&rdquo; to &ldquo;General&rdquo;.</li>
        <li>Click &ldquo;Finish&rdquo;; to import the data.</li>
      </ol>

      <p>
        <RawDataDownloadLink />
      </p>
      <p>
        <a
          href={withPrefix('/Enforcement%20Data%20Dictionary.pdf')}
          id="raw-data-dictionary-link"
          target="_blank"
          rel="noopener noreferrer"
        >
          Enforcement Data Dictionary
        </a>{' '}
        (link will open in a new browser tab or window)
      </p>
    </>
  ),
  expanded: false,
  id: 'raw-data',
  headingLevel: 'h2',
};

const GetOldActionsAccordionContent = () => {
  const acc_data = GetEnforcementPageQueryData();
  let col_defs = [
    {
      field: 'region',
      headerName: 'Region',
      sortable: true,
      cellRenderer: getRegionName,
      filter: customMatcherFilter,
      suppressMenu: true,
      floatingFilter: true,
      floatingFilterComponent: customDropdownFloatingFilter,
      floatingFilterComponentParams: {
        filter_id: 'region',
        suppressFilterButton: true,
        options: region_arr,
        initial_value: 'All Regions',
      },
    },
    {
      field: 'caseNumber',
      headerName: 'Case Number',
      resizable: true,
      cellRenderer: renderLinkedCellDataNewWindow,
      cellRendererParams: (params) => {
        return {
          path: `${withPrefix(params.data.documentUrl)}`,
          value: params.data.caseNumber,
          displayName: params.data.caseNumber,
        };
      },
      filter: customTextFilter,
      suppressMenu: true,
      floatingFilter: true,
      floatingFilterComponent: textFloatingFilter,
    },
    {
      field: 'operator',
      headerName: 'Operator',
      filter: customTextFilter,
      suppressMenu: true,
      floatingFilter: true,
      floatingFilterComponent: textFloatingFilter,
    },
    {
      field: 'documentType',
      headerName: 'Document Type',
      filter: customMatcherFilter,
      suppressMenu: true,
      floatingFilter: true,
      floatingFilterComponent: customDropdownFloatingFilter,
      floatingFilterComponentParams: {
        filter_id: 'doc-type',
        suppressFilterButton: true,
        options: [
          'Amended Final Order',
          'Consent Agreement',
          'Consent Order',
          'Corrective Action Order',
          'Decision on Petition',
          'Final Order',
        ],
        initial_value: 'All Documents',
      },
    },
  ];

  const csv_column_defs = col_defs.slice();

  let csv_name_root = 'cases-2001-and-older';
  let grid_class_name = 'grid-col-12';
  return {
    title: (
      <div id="pre-2002-actions" style={{display: 'flex', justifyContent: 'space-between'}}>
        Enforcement Actions (2001 & Older)
      </div>
    ),
    content: (
      <>
        <p>
          These pages include archived documents for older enforcement cases that were pursued within each of
          PHMSA&rsquo;s Office of Pipeline Safety regions. Unlike the records contained on the{' '}
          <Link to="/cases">Enforcement Actions (2002 — Present)</Link> page, these documents have not been checked for
          accuracy or completeness due to differing recordkeeping practices and procedures that were in place at the
          time. Further, these pages do not represent a complete listing of all of the enforcement cases initiated
          during this time period. To see the document(s) archived for a particular case, select the case of interest in
          the &ldquo;Case Number&rdquo; column.
        </p>
        <div id="all_cases_table_desc" className="usa-prose">
          <CaseListGrid
            data={acc_data.site.siteMetadata.actions_2001_and_older}
            col_defs={col_defs}
            key={'case_list_grid'}
            csv_name_root={csv_name_root}
            grid_class_name={grid_class_name}
            csv_only_column_defs={csv_column_defs}
          />
        </div>
      </>
    ),
    expanded: false,
    id: 'pre-2002-actions',
    headingLevel: 'h2',
  };
};

const GetExcavationAccordionContent = () => {
  const col_defs = [
    {
      field: 'cpfNum',
      headerName: 'Case Number',
      flex: 1,
      cellRenderer: renderLinkedCellData,
      cellRendererParams: (params) => {
        return {
          path: `/excavation/case/${params.value}`,
        };
      },
    },
    {
      field: 'operatorName',
      headerName: 'Excavator',
      flex: 3,
    },
  ];
  const acc_data = GetEnforcementPageQueryData();
  return {
    title: <div style={{display: 'flex', justifyContent: 'space-between'}}>Third Party Excavation Enforcement</div>,
    content: (
      <>
        <h3>
          Cases Initiated{' '}
          <sup>
            (
            <a href="#fn1" id="fn1-link">
              1
            </a>
            )
          </sup>
        </h3>
        <CaseListGrid
          data={acc_data.postgres.excavation_cases}
          col_defs={col_defs}
          key={'excavation_case_list_grid'}
          csv_name_root={'third-party-excavation-enforcement-cases'}
          grid_class_name={'grid-col-12 excavation-grid'}
        />
        <h3>Sources</h3>
        <ol className="font-body-2xs" id="footnotes">
          <li id="fn1">
            <DataCurrencyFootnoteExcavation />
          </li>
        </ol>
      </>
    ),
    expanded: false,
    id: 'excavation-cases',
    headingLevel: 'h2',
  };
};

const EnfHome = () => {
  return (
    <Layout pageTitle="Enforcement Activity">
      <p className="usa-intro">
        PHMSA&rsquo;s Pipeline Safety Enforcement Program is a key component of our oversight mission.
      </p>
      <p>
        The Pipeline Safety Enforcement Program is designed to monitor and enforce compliance with pipeline safety
        regulations and confirm operators are meeting PHMSA expectations for safe, reliable, and environmentally sound
        operation of their facilities.
      </p>
      <p>
        On this website PHMSA offers a variety of reports and records detailing its enforcement activity involving
        pipeline operators. These reports and records are offered on both nationwide and operator-specific bases and
        includes the following:
      </p>
      {linkList()}
      <Accordion
        items={[rawDataAccordion, GetOldActionsAccordionContent(), GetExcavationAccordionContent()]}
        bordered
      />
      <p className="margin-top-4">
        PHMSA monitors compliance through field inspections of facilities and construction projects; programmatic
        inspections of operator management systems, procedures, and processes; incident investigations; and through
        direct dialogue with operator management. To facilitate compliance, PHMSA clarifies its regulatory expectations
        with published inspection forms and protocols; rule interpretations; guidance manuals and advisory notices;
        FAQs; posting of final orders on its website; and public meetings and workshops. Finally, PHMSA has available a
        full range of enforcement mechanisms to require that operators take appropriate and timely corrective actions
        for violations, and that they take preventive measures to preclude future failures or non-compliant operation of
        their pipelines.
      </p>
      <p>
        To find out if a gas transmission pipeline or hazardous liquid trunkline is located near you, visit the{' '}
        <a href="https://www.npms.phmsa.dot.gov/">National Pipeline Mapping System</a> and search by your county or zip
        code.
      </p>
    </Layout>
  );
};

export const Head = () => {
  return <GlobalHead />;
};

export default EnfHome;
